import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { referralsApi } from '../api/referrals';
import { useAuthStore } from '../store/authStore';
import { useReferralEvents } from '../hooks/useReferralEvents';

export default function AffiliateDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const [copied, setCopied] = useState(false);

  // Real-time updates via SSE
  const handleReferralUpdate = useCallback(() => {
    // Invalidate query to refetch full data including conversions table
    queryClient.invalidateQueries({ queryKey: ['referralStats'] });
  }, [queryClient]);

  useReferralEvents(handleReferralUpdate);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/affiliate/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Check application status
  const { data: applicationStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['affiliateApplicationStatus'],
    queryFn: referralsApi.getApplicationStatus,
    enabled: isAuthenticated,
  });

  // Get referral stats (initial load, then updated via SSE)
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['referralStats'],
    queryFn: referralsApi.getMyStats,
    enabled: isAuthenticated && applicationStatus?.status === 'approved',
  });

  // Redirect to apply page if not an affiliate
  useEffect(() => {
    if (applicationStatus && applicationStatus.status !== 'approved') {
      navigate('/affiliate/apply');
    }
  }, [applicationStatus, navigate]);

  const referralLink = stats?.code
    ? `${window.location.origin}?ref=${stats.code}`
    : null;

  const handleCopy = async () => {
    if (referralLink) {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loadingStatus || loadingStats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">
            {t('affiliate.dashboard.title', 'Affiliate Dashboard')}
          </h1>
        </div>

        {/* Referral Link */}
        <section className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            {t('affiliate.dashboard.yourLink', 'Your Referral Link')}
          </h2>
          <div className="flex gap-3">
            <input
              type="text"
              readOnly
              value={referralLink || ''}
              className="flex-1 px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl outline-none"
            />
            <button
              onClick={handleCopy}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white'
              }`}
            >
              {copied ? '✓ ' + t('common.copied', 'Copied!') : t('affiliate.dashboard.copy', 'Copy')}
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-3">
            {t('affiliate.dashboard.code', 'Your code')}: <span className="font-mono text-primary-400">{stats?.code}</span>
          </p>
        </section>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-sm text-gray-400 mb-1">
              {t('affiliate.dashboard.conversions', 'Conversions')}
            </p>
            <p className="text-3xl font-bold text-white">
              {stats?.conversionsCount || 0}
            </p>
          </div>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-sm text-gray-400 mb-1">
              {t('affiliate.dashboard.totalEarnings', 'Total Earnings')}
            </p>
            <p className="text-3xl font-bold text-green-400">
              €{(stats?.totalEarnings || 0).toFixed(2)}
            </p>
          </div>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-sm text-gray-400 mb-1">
              {t('affiliate.dashboard.pendingPayout', 'Pending Payout')}
            </p>
            <p className="text-3xl font-bold text-primary-400">
              €{(stats?.pendingEarnings || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('affiliate.dashboard.minimumPayout', 'Minimum payout: €50')}
            </p>
          </div>
        </div>

        {/* Commission Info */}
        <section className="backdrop-blur-xl bg-gradient-to-r from-primary-900/30 to-secondary-900/30 border border-primary-500/20 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">💰</span>
            <h3 className="text-lg font-semibold text-white">
              {t('affiliate.dashboard.commissionRate', '10% Recurring Commission')}
            </h3>
          </div>
          <p className="text-gray-300 text-sm">
            {t('affiliate.dashboard.commissionDescription', 'You earn 10% of every payment made by users who sign up using your referral link. This includes monthly renewals!')}
          </p>
        </section>

        {/* Conversions Table */}
        <section className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            {t('affiliate.dashboard.recentConversions', 'Recent Conversions')}
          </h2>

          {stats?.conversions && stats.conversions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-400 border-b border-white/10">
                    <th className="pb-3 pr-4">{t('affiliate.dashboard.date', 'Date')}</th>
                    <th className="pb-3 pr-4">{t('affiliate.dashboard.revenue', 'Revenue')}</th>
                    <th className="pb-3">{t('affiliate.dashboard.commission', 'Commission')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.conversions.map((conversion, index) => (
                    <tr key={index} className="border-b border-white/5">
                      <td className="py-3 pr-4 text-gray-300">
                        {new Date(conversion.convertedAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-4 text-white">
                        €{conversion.revenue.toFixed(2)}
                      </td>
                      <td className="py-3 text-green-400 font-medium">
                        €{conversion.commissionEarned.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📊</div>
              <p className="text-gray-400">
                {t('affiliate.dashboard.noConversions', 'No conversions yet. Share your link to start earning!')}
              </p>
            </div>
          )}
        </section>

        {/* Paid Earnings */}
        {(stats?.paidEarnings || 0) > 0 && (
          <section className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 mt-6">
            <h2 className="text-lg font-semibold text-white mb-2">
              {t('affiliate.dashboard.paidEarnings', 'Paid Earnings')}
            </h2>
            <p className="text-2xl font-bold text-gray-300">
              €{(stats?.paidEarnings || 0).toFixed(2)}
            </p>
          </section>
        )}

        {/* Help */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>
            {t('affiliate.dashboard.helpText', 'Questions about payouts? Contact us at')}{' '}
            <a href="mailto:affiliates@fonorex.com" className="text-primary-400 hover:underline">
              affiliates@fonorex.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
